/* Where a chat picture is fetched from.

   Not the shared /download link the rest of the app uses for files: that
   route answers with a Content-Disposition of attachment, which is right for
   a resume and wrong for something that has to appear inside a bubble. */
export const chatImageUrl = (file) => (file ? `/api/v1/chat/image/${file}` : '');

/* How long after sending a message may still be edited or deleted.

   The server enforces this — it is the only side that can be trusted with a
   clock — and this copy exists purely so the menu can grey itself out before
   someone picks an option that was always going to be refused. */
export const AMEND_WINDOW_MS = 24 * 60 * 60 * 1000;

export const isMine = (message, myCode) =>
    Boolean(message && myCode && String(message.senderCode) === String(myCode));

/* Whether the edit and delete options should be offered at all.

   A message still on its way has no id on the server yet, so there is nothing
   to amend; one already deleted has nothing left to change. */
export const canAmend = (message, myCode) => {
    if (!isMine(message, myCode)) return false;
    if (message.deleted) return false;
    if (!message._id || String(message._id).startsWith('pending-')) return false;
    if (message.status === 'pending' || message.status === 'failed') return false;
    const at = new Date(message.createdAt).getTime();
    if (!at) return false;
    return Date.now() - at <= AMEND_WINDOW_MS;
};

/* What is left of the 24 hours, worded for a tooltip rather than a countdown
   — "23h left" is useful, "23:14:07" is a stopwatch nobody asked for. */
export const amendWindowLeft = (message) => {
    const at = new Date(message?.createdAt).getTime();
    if (!at) return '';
    const left = AMEND_WINDOW_MS - (Date.now() - at);
    if (left <= 0) return '';
    const hours = Math.floor(left / 3600000);
    if (hours >= 1) return `${hours}h left to edit or delete`;
    const minutes = Math.max(1, Math.round(left / 60000));
    return `${minutes}m left to edit or delete`;
};

/* A picture is read once in the browser before it is sent, so the bubble can
   be laid out at the right shape straight away instead of collapsing to
   nothing and then snapping open when the bytes arrive. */
export const measureImage = (file) => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
        resolve({ width: image.naturalWidth, height: image.naturalHeight, previewUrl: url });
    };
    /* A file the browser cannot decode is still worth sending — the server
       has already agreed it is an image type — it just gets no head start on
       its dimensions. */
    image.onerror = () => resolve({ width: 0, height: 0, previewUrl: url });
    image.src = url;
});

export const IMAGE_MIME = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

export const isSendableImage = (file) =>
    Boolean(file) && IMAGE_MIME.includes(String(file.type).toLowerCase());

/* Pull images out of a paste or a drop. A screenshot pasted from the
   clipboard arrives as a file with no name, which is why one is invented
   here rather than left blank in the thread. */
export const imagesFromTransfer = (dataTransfer) => {
    if (!dataTransfer) return [];
    const items = Array.from(dataTransfer.files || []);
    const found = items.filter(isSendableImage);
    if (found.length) return found;

    return Array.from(dataTransfer.items || [])
        .filter((item) => item.kind === 'file' && isSendableImage({ type: item.type }))
        .map((item) => item.getAsFile())
        .filter(Boolean);
};

export const prettySize = (bytes) => {
    const size = Number(bytes) || 0;
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};
